import { useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ScrollableTable, Td, Th, Tr } from 'components/tables'
import { TableFilterField } from 'components/tables/TableFilterField'
import { WorkdayValue } from './types'

// Workday values are a mix of strings, numbers, booleans and nulls
// (with dates and decimals as strings), so compare them as strings,
// numerically where they hold numbers
export const compareWorkdayValues = <T,>(
  ...[rowA, rowB, columnId]: Parameters<SortingFn<T>>
) =>
  String(rowA.getValue<WorkdayValue>(columnId) ?? '').localeCompare(
    String(rowB.getValue<WorkdayValue>(columnId) ?? ''),
    undefined,
    { numeric: true },
  )

// The built-in 'includesString' filter calls toLowerCase on the value,
// which throws on numbers and booleans, so search the text the cell shows.
// It keeps that name so TableFilterField still renders a search field.
const includesWorkdayValue: FilterFn<unknown> = (row, columnId, filterValue) =>
  String(row.getValue<WorkdayValue>(columnId))
    .toLowerCase()
    .includes(String(filterValue).toLowerCase())

includesWorkdayValue.autoRemove = (value) => !value

export const WorkdayValueCell = ({ value }: { value: WorkdayValue }) => {
  if (value === null)
    return <span className="italic text-neutral-400">null</span>

  return <>{String(value)}</>
}

interface Props<T> {
  columns: ColumnDef<T, WorkdayValue>[]
  data: T[]
  initialSorting: SortingState
}

// A sortable table with a search field on every column
export const WorkdayDataTable = <T,>({
  columns,
  data,
  initialSorting,
}: Props<T>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    filterFns: { includesString: includesWorkdayValue },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
  })

  return (
    <ScrollableTable
      rootClassName="mx-0"
      header={table.getHeaderGroups().map((headerGroup) => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <Th
              key={header.id}
              clickable={header.column.getCanSort()}
              label={flexRender(
                header.column.columnDef.header,
                header.getContext(),
              )}
              onClick={header.column.getToggleSortingHandler()}
              sortDir={header.column.getIsSorted()}
            >
              {header.column.getCanFilter() && (
                <TableFilterField
                  column={header.column}
                  inputClassName="w-20"
                />
              )}
            </Th>
          ))}
        </Tr>
      ))}
      body={table.getRowModel().rows.map((row) => (
        <Tr key={row.id} className="hover:bg-neutral-100">
          {row.getVisibleCells().map((cell) => (
            <Td className="px-3 py-2 whitespace-nowrap" key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Td>
          ))}
        </Tr>
      ))}
    />
  )
}
